import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CaretDown, CaretUp, PencilSimple } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useEditConfirm } from "../../context/EditConfirmContext";
import { formatET } from "../../lib/dateUtils";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

const EDIT_FIELDS = [
  { key: "revenue", label: "Revenue", type: "number" },
  { key: "orders_placed", label: "Orders Placed", type: "number" },
  { key: "orders_fulfilled", label: "Orders Fulfilled", type: "number" },
  { key: "inventory_status", label: "Inventory Status", type: "text" },
  { key: "monthly_profit_to_date", label: "Monthly Profit To Date", type: "number" },
];

export function SubmissionsList({ reports, onRefresh }) {
  const confirmEdit = useEditConfirm();
  const [expanded, setExpanded] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const act = async (id, action) => {
    const r = reports.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this"} report (${action})`))) return;
    try {
      await api.patch(`/admin/reports/${id}/${action}`);
      toast.success(action === "approve" ? "Report approved and live to client" : "Report placed on hold");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const startEdit = (r) => {
    setExpanded(r.id);
    setEditingId(r.id);
    setEditForm({
      revenue: r.revenue,
      orders_placed: r.orders_placed,
      orders_fulfilled: r.orders_fulfilled,
      inventory_status: r.inventory_status,
      monthly_profit_to_date: r.monthly_profit_to_date,
      performance_notes: r.performance_notes || "",
      store_update_text: r.store_update_text || "",
      projections_notes: r.projections_notes || "",
    });
  };

  const saveEdit = async (id) => {
    if (!(await confirmEdit("this report"))) return;
    try {
      await api.patch(`/admin/reports/${id}`, {
        ...editForm,
        revenue: parseFloat(editForm.revenue),
        orders_placed: parseInt(editForm.orders_placed, 10),
        orders_fulfilled: parseInt(editForm.orders_fulfilled, 10),
        monthly_profit_to_date: parseFloat(editForm.monthly_profit_to_date),
      });
      toast.success("Report updated");
      setEditingId(null);
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitReject = async (id) => {
    if (!rejectNote.trim()) {
      toast.error("Add a note explaining why this report is being sent back");
      return;
    }
    try {
      await api.patch(`/admin/reports/${id}/reject`, { note: rejectNote });
      toast.success("Report rejected and sent back to Back Office");
      setRejectingId(null);
      setRejectNote("");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="bg-igc-surface border border-igc-gold/15 p-6" data-testid="submissions-list">
      <p className="text-igc-muted text-xs uppercase tracking-widest mb-4" title="Weekly performance reports submitted by each client's Back Office team, waiting on your review before the client can see them.">Back Office Submissions</p>
      {reports.length === 0 && <p className="text-igc-muted text-sm">No submissions yet.</p>}
      <div className="space-y-2">
        {reports.map((r) => (
          <div key={r.id} className="border border-igc-gold/10" data-testid={`submission-row-${r.id}`}>
            <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
              <div className="flex items-center gap-4">
                {expanded === r.id ? <CaretUp size={14} className="text-igc-muted" /> : <CaretDown size={14} className="text-igc-muted" />}
                <div>
                  <p className="text-igc-cream text-sm">{r.store_display_name} — {r.month_label} (week ending {r.week_ending})</p>
                  <p className="text-igc-muted text-xs">Submitted {formatET(r.submitted_at, "MMM d, yyyy h:mm a")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] px-2 py-1 tracking-wide ${r.status === "approved" ? "bg-igc-success/10 text-igc-success" : r.status === "rejected" || r.status === "hold" ? "bg-igc-danger/10 text-igc-danger" : "bg-igc-gold/10 text-igc-goldText"}`} data-testid={`submission-status-${r.id}`} title="Pending = awaiting your review. Approved = live and visible to the client. Rejected/Hold = sent back, not visible to the client yet.">{r.status}</span>
                {r.status === "pending" && (
                  <button data-testid={`edit-toggle-${r.id}`} onClick={(e) => { e.stopPropagation(); startEdit(r); }} title="Lets you correct any number or note on this report before approving it." className="flex items-center gap-1 text-xs border border-igc-gold/40 text-igc-goldText px-3 py-1.5 hover:bg-igc-gold hover:text-black transition-colors">
                    <PencilSimple size={12} /> Edit
                  </button>
                )}
                {r.status === "pending" && (
                  <button data-testid={`approve-toggle-${r.id}`} onClick={(e) => { e.stopPropagation(); act(r.id, "approve"); }} title="Approves this report, making it immediately visible to the client on their dashboard." className="text-xs border border-igc-success text-igc-success px-3 py-1.5 hover:bg-igc-success hover:text-black transition-colors">Approve</button>
                )}
                {r.status === "pending" && (
                  <button data-testid={`reject-toggle-${r.id}`} onClick={(e) => { e.stopPropagation(); setExpanded(r.id); setRejectingId(r.id); }} title="Sends this report back to Back Office with a note explaining what needs to be fixed." className="text-xs border border-igc-danger text-igc-danger px-3 py-1.5 hover:bg-igc-danger hover:text-black transition-colors">Reject</button>
                )}
                {r.status === "approved" && (
                  <button data-testid={`hold-toggle-${r.id}`} onClick={(e) => { e.stopPropagation(); act(r.id, "hold"); }} title="Pulls this already-approved report back off the client's dashboard, putting it on hold." className="text-xs border border-igc-danger text-igc-danger px-3 py-1.5 hover:bg-igc-danger hover:text-black transition-colors">Hold</button>
                )}
              </div>
            </div>
            {expanded === r.id && (
              <div className="p-4 border-t border-igc-gold/10 space-y-3 text-sm">
                {r.status === "rejected" && r.rejection_note && (
                  <div className="bg-igc-danger/10 border border-igc-danger/30 p-3" data-testid={`rejection-note-${r.id}`}>
                    <p className="text-igc-danger text-xs uppercase tracking-widest mb-1">Rejection Note</p>
                    <p className="text-igc-cream text-sm">{r.rejection_note}</p>
                  </div>
                )}
                {editingId === r.id ? (
                  <div className="grid grid-cols-2 gap-3">
                    {EDIT_FIELDS.map((f) => (
                      <div key={f.key}>
                        <p className="text-igc-muted text-xs mb-1">{f.label}</p>
                        <Input data-testid={`edit-input-${f.key}-${r.id}`} type={f.type} value={editForm[f.key]} onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
                      </div>
                    ))}
                    <div className="col-span-2">
                      <p className="text-igc-muted text-xs mb-1">Performance Notes (internal)</p>
                      <Textarea data-testid={`edit-input-performance_notes-${r.id}`} value={editForm.performance_notes} onChange={(e) => setEditForm({ ...editForm, performance_notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
                    </div>
                    <div className="col-span-2">
                      <p className="text-igc-muted text-xs mb-1">Store Update (client-facing)</p>
                      <Textarea data-testid={`edit-input-store_update_text-${r.id}`} value={editForm.store_update_text} onChange={(e) => setEditForm({ ...editForm, store_update_text: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
                    </div>
                    <div className="col-span-2">
                      <p className="text-igc-muted text-xs mb-1">Projections Notes</p>
                      <Textarea data-testid={`edit-input-projections_notes-${r.id}`} value={editForm.projections_notes} onChange={(e) => setEditForm({ ...editForm, projections_notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <Button data-testid={`save-edit-button-${r.id}`} onClick={() => saveEdit(r.id)} title="Saves your corrections to this report's numbers and notes." className="bg-igc-gold hover:bg-igc-goldHover text-black">Save Changes</Button>
                      <Button data-testid={`cancel-edit-button-${r.id}`} onClick={() => setEditingId(null)} variant="outline" title="Discards your changes and closes the edit form." className="border-igc-gold/30 text-igc-muted">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div><p className="text-igc-muted text-xs">Revenue</p><p className="text-igc-cream">${Number(r.revenue || 0).toLocaleString()}</p></div>
                    <div><p className="text-igc-muted text-xs">Orders Placed / Fulfilled</p><p className="text-igc-cream">{r.orders_placed} / {r.orders_fulfilled}</p></div>
                    <div><p className="text-igc-muted text-xs">Inventory Status</p><p className="text-igc-cream">{r.inventory_status}</p></div>
                    <div><p className="text-igc-muted text-xs">Monthly Profit To Date</p><p className="text-igc-cream">${Number(r.monthly_profit_to_date || 0).toLocaleString()}</p></div>
                    <div className="col-span-2"><p className="text-igc-muted text-xs">Performance Notes (internal)</p><p className="text-igc-cream">{r.performance_notes || "—"}</p></div>
                    <div className="col-span-2"><p className="text-igc-muted text-xs">Store Update (client-facing)</p><p className="text-igc-cream">{r.store_update_text || "—"}</p></div>
                    <div className="col-span-2"><p className="text-igc-muted text-xs">Projections Notes</p><p className="text-igc-cream">{r.projections_notes || "—"}</p></div>
                  </div>
                )}
                {rejectingId === r.id && (
                  <div className="border-t border-igc-gold/10 pt-3" data-testid={`reject-note-panel-${r.id}`}>
                    <p className="text-igc-muted text-xs mb-1">Rejection Note (sent back to Back Office)</p>
                    <Textarea data-testid={`reject-note-input-${r.id}`} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream mb-2" />
                    <div className="flex gap-2">
                      <Button data-testid={`reject-note-submit-${r.id}`} onClick={() => submitReject(r.id)} title="Sends this report back to Back Office along with your rejection note above." className="bg-igc-danger hover:bg-igc-danger/80 text-black">Send Back to Back Office</Button>
                      <Button data-testid={`reject-note-cancel-${r.id}`} onClick={() => { setRejectingId(null); setRejectNote(""); }} variant="outline" title="Cancels the rejection and closes this panel without sending anything back." className="border-igc-gold/30 text-igc-muted">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
}
